import { escapeHtml } from '@/utils/htmlEscape';

export interface ReportMeta {
  title: string;
  organizationName: string;
  logoUrl?: string | null;
  facilityScope: string;
  dateRangeLabel: string;
  generatedBy: string;
  generatedAt?: Date;
  summary?: Record<string, string | number>;
}

export interface ReportPayload extends ReportMeta {
  headers: string[];
  rows: (string | number)[][];
}

const stamp = () => new Date().toISOString().slice(0, 10);

export const slugify = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'report';

/* ------------------------------------------------------------------ CSV */

const csvCell = (value: string | number) => {
  const text = String(value ?? '');
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export function buildCsv(payload: ReportPayload): string {
  const lines: string[] = [];
  lines.push(csvCell(payload.title));
  lines.push(`Organization,${csvCell(payload.organizationName)}`);
  lines.push(`Facility,${csvCell(payload.facilityScope)}`);
  lines.push(`Date range,${csvCell(payload.dateRangeLabel)}`);
  lines.push(`Generated by,${csvCell(payload.generatedBy)}`);
  lines.push(`Generated at,${csvCell((payload.generatedAt ?? new Date()).toLocaleString())}`);
  lines.push('');
  if (payload.summary && Object.keys(payload.summary).length) {
    lines.push('Summary');
    Object.entries(payload.summary).forEach(([k, v]) => lines.push(`${csvCell(k)},${csvCell(v)}`));
    lines.push('');
  }
  lines.push(payload.headers.map(csvCell).join(','));
  payload.rows.forEach((row) => lines.push(row.map(csvCell).join(',')));
  return lines.join('\n');
}

function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function downloadCsv(payload: ReportPayload) {
  const blob = new Blob([`\uFEFF${buildCsv(payload)}`], { type: 'text/csv;charset=utf-8;' });
  triggerDownload(blob, `${slugify(payload.title)}_${stamp()}.csv`);
}

/* ---------------------------------------------------------------- Excel */

export async function downloadExcel(payload: ReportPayload) {
  const ExcelJS = await import('exceljs');
  const workbook = new ExcelJS.Workbook();
  workbook.creator = payload.organizationName;
  const sheet = workbook.addWorksheet(payload.title.slice(0, 28) || 'Report');

  const titleRow = sheet.addRow([payload.title]);
  titleRow.font = { bold: true, size: 14 };
  sheet.addRow([payload.organizationName]);
  sheet.addRow([`Facility: ${payload.facilityScope}`]);
  sheet.addRow([`Date range: ${payload.dateRangeLabel}`]);
  sheet.addRow([`Generated by ${payload.generatedBy} on ${(payload.generatedAt ?? new Date()).toLocaleString()}`]);
  sheet.addRow([]);

  if (payload.summary && Object.keys(payload.summary).length) {
    const head = sheet.addRow(['Summary']);
    head.font = { bold: true };
    Object.entries(payload.summary).forEach(([k, v]) => sheet.addRow([k, v]));
    sheet.addRow([]);
  }

  const header = sheet.addRow(payload.headers);
  header.font = { bold: true };
  header.eachCell((cell) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
  });
  payload.rows.forEach((row) => sheet.addRow(row));

  payload.headers.forEach((h, i) => {
    const widths = payload.rows.map((r) => String(r[i] ?? '').length);
    sheet.getColumn(i + 1).width = Math.min(42, Math.max(12, h.length + 2, ...widths.map((w) => w + 2)));
  });

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownload(
    new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
    `${slugify(payload.title)}_${stamp()}.xlsx`
  );
}

/* ---------------------------------------------------------------- Print */

export function printReport(payload: ReportPayload) {
  const generatedAt = payload.generatedAt ?? new Date();
  const summaryHtml =
    payload.summary && Object.keys(payload.summary).length
      ? `<div class="summary">${Object.entries(payload.summary)
          .map(
            ([k, v]) =>
              `<div class="stat"><span class="stat-label">${escapeHtml(k)}</span><span class="stat-value">${escapeHtml(v)}</span></div>`
          )
          .join('')}</div>`
      : '';

  const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8" /><title>${escapeHtml(payload.title)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Helvetica, Arial, sans-serif; color: #1c1917; margin: 32px; font-size: 12px; }
  header { display: flex; align-items: flex-start; justify-content: space-between; border-bottom: 2px solid #1c1917; padding-bottom: 12px; margin-bottom: 16px; gap: 16px; }
  .org { font-size: 15px; font-weight: 700; letter-spacing: .02em; }
  h1 { font-size: 20px; margin: 6px 0 2px; }
  .meta { text-align: right; font-size: 11px; color: #57534e; line-height: 1.6; }
  img.logo { max-height: 44px; max-width: 180px; object-fit: contain; margin-bottom: 6px; display: block; }
  .summary { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
  .stat { border: 1px solid #e7e5e4; border-radius: 6px; padding: 8px 14px; min-width: 130px; }
  .stat-label { display: block; font-size: 10px; text-transform: uppercase; letter-spacing: .06em; color: #78716c; }
  .stat-value { display: block; font-size: 17px; font-weight: 700; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; }
  th { text-align: left; background: #f5f5f4; border-bottom: 1px solid #d6d3d1; padding: 6px 8px; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
  td { padding: 6px 8px; border-bottom: 1px solid #f0efee; vertical-align: top; }
  tr:nth-child(even) td { background: #fbfaf9; }
  footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e7e5e4; font-size: 10px; color: #78716c; display: flex; justify-content: space-between; }
  .empty { padding: 28px; text-align: center; color: #78716c; border: 1px dashed #d6d3d1; border-radius: 6px; }
  @media print { body { margin: 12mm; } thead { display: table-header-group; } tr { break-inside: avoid; } }
</style></head>
<body>
  <header>
    <div>
      ${payload.logoUrl ? `<img class="logo" src="${escapeHtml(payload.logoUrl)}" alt="${escapeHtml(payload.organizationName)} logo" />` : ''}
      <div class="org">${escapeHtml(payload.organizationName)}</div>
      <h1>${escapeHtml(payload.title)}</h1>
    </div>
    <div class="meta">
      <div><strong>Facility:</strong> ${escapeHtml(payload.facilityScope)}</div>
      <div><strong>Date range:</strong> ${escapeHtml(payload.dateRangeLabel)}</div>
      <div><strong>Generated by:</strong> ${escapeHtml(payload.generatedBy)}</div>
      <div>${escapeHtml(generatedAt.toLocaleString())}</div>
    </div>
  </header>
  ${summaryHtml}
  ${
    payload.rows.length === 0
      ? '<div class="empty">No records match the selected filters.</div>'
      : `<table><thead><tr>${payload.headers.map((h) => `<th>${escapeHtml(h)}</th>`).join('')}</tr></thead>
         <tbody>${payload.rows
           .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
           .join('')}</tbody></table>`
  }
  <footer>
    <span>${escapeHtml(payload.organizationName)} — confidential</span>
    <span>${payload.rows.length} record${payload.rows.length === 1 ? '' : 's'}</span>
  </footer>
  <script>window.onload = function () { window.focus(); window.print(); };</script>
</body></html>`;

  const win = window.open('', '_blank', 'width=1100,height=800');
  if (!win) return false;
  win.document.open();
  win.document.write(html);
  win.document.close();
  return true;
}
